import React, { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext, css } from 'styled-components';
import { renderStyledElementStyles } from '../../../helpers';
import {
  StyledImageTag,
  StyledParagraph,
  CallToAction,
} from '../../../siteBuilderModules/sharedComponents';

const Container = styled.div`
  display: block;
  padding: 25px 30px;
  margin-bottom: 20px;
  border-radius: 3px;
  box-shadow: rgba(0, 0, 0, 0.3) 0 1px 2px 0;
  background-color: #fff;
  color: #000;
  min-height: 140px;
  height: calc(100% - 20px);

  ${({ theme }) =>
    renderStyledElementStyles(theme, theme.siteViews.contactView)}

  .contact-image {
    border-radius: 50%;
    background: center no-repeat;
    background-size: cover;
    margin: 0;

    ${({ theme }) =>
      renderStyledElementStyles(
        theme,
        theme.siteViews.contactView.elements.image,
      )}
  }
`;

const Content = styled.div`
  display: table-cell;
  vertical-align: top;
`;

const TextBlock = styled.div`
  margin-top: 1em;

  a {
    ${({ theme }) =>
      renderStyledElementStyles(
        theme,
        theme.siteViews.contactView.elements.link,
      )}
  }
`;

const Label = styled.span`
  color: #999;

  ${({ theme }) =>
    theme.globalVariables.isRTL
      ? css`
          margin-left: 10px;
        `
      : css`
          margin-right: 10px;
        `}
`;

const Icon = styled.span`
  position: relative;
  top: -1px;
  font-size: 18px;
  vertical-align: middle;

  &.fa-square-whatsapp {
    font-size: 22px;
    top: -2px;
  }

  ${({ theme }) => css`
    color: ${theme?.brandColors?.accent || 'inherit'};
  `}

  ${({ theme }) =>
    theme.globalVariables.isRTL
      ? css`
          margin-left: 10px;
        `
      : css`
          margin-right: 10px;
        `}
`;

export function ContactBlock({ contact }) {
  const {
    photo,
    type,
    name,
    email,
    phoneNumber,
    faxNumber,
    firstLine,
    secondLine,
    thirdLine,
    fourthLine,
    city,
    postcode,
    whatsAppUrl,
    whatsAppText,
  } = contact;

  const displayStyles = useContext(ThemeContext).siteViews.contactView;

  const renderAddressSegment = (text) => (
    <Fragment>
      ,
      <br />
      <span>{text}</span>
    </Fragment>
  );

  const renderAddressSection = () => {
    if (firstLine || secondLine || city || postcode) {
      return (
        <TextBlock data-testid="contact-address">
          {firstLine && <span>{firstLine}</span>}
          {secondLine && renderAddressSegment(secondLine)}
          {thirdLine && renderAddressSegment(thirdLine)}
          {fourthLine && renderAddressSegment(fourthLine)}
          {city && renderAddressSegment(city)}
          {postcode && (
            <Fragment>
              ,&nbsp;
              <span>{postcode}</span>
            </Fragment>
          )}
        </TextBlock>
      );
    }
  };

  return (
    <Container>
      {photo && (
        <StyledImageTag
          imageConfig={photo}
          imgixParams="w=80&h=80"
          displayStyles={displayStyles}
          className="contact-image"
        />
      )}
      <Content>
        {type && (
          <StyledParagraph
            content={type}
            displayStyles={displayStyles}
            testId="contact-type"
          />
        )}
        {name && <StyledParagraph content={name} testId="contact-name" />}
        {email && (
          <TextBlock data-testid="contact-email">
            <CallToAction type="link" to={`mailto:${email}`} target="_blank">
              <Icon className="fa-solid fa-envelope" />
              {email}
            </CallToAction>
          </TextBlock>
        )}
        {phoneNumber && (
          <TextBlock data-testid="contact-phone">
            <CallToAction type="link" to={`tel:${phoneNumber}`} target="_blank">
              <Icon className="fa-solid fa-phone" />
              {phoneNumber}
            </CallToAction>
          </TextBlock>
        )}
        {whatsAppUrl && (
          <TextBlock data-testid="contact-whatsapp">
            <CallToAction type="link" to={whatsAppUrl} target="_blank">
              <Icon className="fab fa-square-whatsapp" />
              {whatsAppText || 'WhatsApp'}
            </CallToAction>
          </TextBlock>
        )}
        {renderAddressSection()}
        {faxNumber && (
          <TextBlock data-testid="contact-fax">
            <Label>Fax:</Label>
            <span>{faxNumber}</span>
          </TextBlock>
        )}
      </Content>
    </Container>
  );
}

ContactBlock.propTypes = {
  contact: PropTypes.object.isRequired,
};

ContactBlock.defaultProps = {
  contact: {},
};

export default ContactBlock;
